// src/pages/Seminar.jsx
import React, { useState } from "react";
import Container from "../components/Container.jsx";
import TrustpilotReviews from "../components/TrustpilotReviews.jsx";
import { textOnlyOptions, formatIntl } from "../lib/countries";
import { openCalendlyPopup} from "../lib/calendly";

/** Strip +CC / 00CC and any leading 0 (keeps digits only) */
function scrubLocal(raw = "", cc = "+44") {
  let v = String(raw).replace(/[\s-]+/g, "");
  const ccNum = String(cc || "+").replace("+", "");
  if (ccNum) {
    const re = new RegExp(`^(?:\\+|00)?${ccNum}\\s*0?`);
    v = v.replace(re, "");
  }
  return v.replace(/^\+/, "");
}

const SEMINAR_SOURCE = "Currency Transfer Seminar Q1 2026";
const SEMINAR_ID = "CT-2026-01-28";
const SEMINAR_DATE = "2026-01-28";

export default function Seminar() {
  // Phone (mirror Enquire/Landing)
  const [countryCode, setCountryCode] = useState("+44");
  const [phoneLocal, setPhoneLocal] = useState("");

  // Form bits (mirror Landing)
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [readyToInvest, setReadyToInvest] = useState("");
  const [amountToInvest, setAmountToInvest] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);

  // Popup hint (toast) when Calendly is clicked without the form filled
const [hint, setHint] = useState("");     // empty string = hidden

function showHint(msg) {
  setHint(msg);
  window.clearTimeout(showHint._t);
  showHint._t = window.setTimeout(() => setHint(""), 3000); // auto-hide
}

function openCalendlyOrHint() {
  // Require the four fields before opening Calendly
  if (!fullName || !email || !phoneLocal || !consent) {
    // Nudge user; focus the first missing field
    showHint("Fill in the form above to book a call.");
    const firstMissing =
      (!fullName && "input[autocomplete='name']") ||
      (!email && "input[autocomplete='email']") ||
      (!phoneLocal && "input[name='phoneLocal']") ||
      (!consent && "input[type='checkbox']") ||
      null;
    if (firstMissing) document.querySelector(firstMissing)?.focus();
    return;
  }

  // All good → prefill Calendly like Landing
  const phoneLocalClean = scrubLocal(phoneLocal, countryCode);
  const phoneIntl = formatIntl(countryCode, phoneLocalClean);

  openCalendlyPopup({
    name: fullName,
    email,
    a1: phoneIntl,        // Phone
    a2: readyToInvest,    // When planning
    a3: amountToInvest,   // Amount
    utm: { utmSource: "seminar", utmMedium: "cta", utmCampaign: "hero" },
  });
}


  // UI
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      const localClean = scrubLocal(phoneLocal, countryCode);
      const phoneE164 = formatIntl(countryCode, localClean);
      const cleanEmail = String(email || "").trim().toLowerCase();

      if (!fullName || !cleanEmail || !phoneLocal || !consent) {
        throw new Error("Please complete the required fields.");
      }

      // UTMs (optional)
      const usp = new URLSearchParams(window.location.search);
      const utm = {
        utm_source: usp.get("utm_source") || "",
        utm_medium: usp.get("utm_medium") || "",
        utm_campaign: usp.get("utm_campaign") || "",
        utm_term: usp.get("utm_term") || "",
        utm_content: usp.get("utm_content") || "",
      };

      const payload = {
        name: fullName,
        email: cleanEmail,
        countryCode,
        phone: phoneE164,
        message: message || `Seminar signup for ${SEMINAR_ID} on ${SEMINAR_DATE}`,
        readyToInvest,
        amountToInvest,
        consent: consent ? "yes" : "no",
        timestamp: new Date().toISOString(),
        route: "seminar",
        source: SEMINAR_SOURCE,
        seminarId: SEMINAR_ID,
        seminarDate: SEMINAR_DATE,
        ...utm,
      };

      const res = await fetch("/enquire", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: new URLSearchParams(payload),
      });

      const text = await res.text();
      let ok = res.ok, serverErr = "";
      try {
        const j = JSON.parse(text);
        if (typeof j?.ok === "boolean") ok = j.ok;
        if (j?.error) serverErr = j.error;
      } catch {}

      if (!ok) throw new Error(serverErr || "Submit failed");

      window.gtag?.("event", "generate_lead", { form_name: "seminar" });
      setSuccess(true);
    } catch (e2) {
      setErr(e2?.message || "Submit error.");
    } finally {
      setLoading(false);
    }
  }

  // 🔒 Calendly: gated + same mapping as Landing (a1 phone, a2 when, a3 amount)
  function openCalendlyFromForm() {
    if (!fullName || !email || !phoneLocal || !consent) {
      setErr("Please complete Name, Email, Phone and consent before booking.");
      const firstMissing =
        (!fullName && "input[autocomplete='name']") ||
        (!email && "input[autocomplete='email']") ||
        (!phoneLocal && "input[name='phoneLocal']") ||
        null;
      if (firstMissing) document.querySelector(firstMissing)?.focus();
      return;
    }

    const phoneLocalClean = scrubLocal(phoneLocal, countryCode);
    const phoneIntl = formatIntl(countryCode, phoneLocalClean);

    openCalendlyPopup({
      name: fullName,
      email,
      a1: phoneIntl,
      a2: readyToInvest,
      a3: amountToInvest,
      utm: { utmSource: "seminar", utmMedium: "cta", utmCampaign: "hero" },
    });
  }

  const heroImage = "content/images/hero-image.jpg";
  const testimonial1_1080 = "/content/videos/testimonial1_1080.mp4";
  const testimonial2_1080 = "/content/videos/testimonial2_1080.mp4";
  const poster1 = "content/images/Screenshot 2025-11-14 at 10.01.35.png";
const poster2 = "content/images/Screenshot 2025-11-14 at 10.00.56.png";

  return (
    <main className="bg-white">
      {/* HERO */}

      {/* toast */}
{hint && (
  <div
    role="status"
    aria-live="polite"
    className="fixed left-1/2 top-6 z-50 -translate-x-1/2 rounded-lg bg-[#003366] px-4 py-2 text-white shadow-lg"
  >
    {hint}
  </div>
)}

      <section className="relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${heroImage}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#003366]/80 via-[#003366]/60 to-white/0" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
          <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-10 items-start">
            {/* Left copy */}
            <div className="text-white">
              <h1 className="text-3xl sm:text-4xl font-semibold">
                Currency Transfer × Ethira Property Group - Overseas Investor Seminar

              </h1>
              <p className="mt-3 max-w-xl text-white/90 text-2xl">
                Join us on <strong>28 January 2026</strong> to see how informed expat investors are structuring UK property portfolios with clarity, strategy, and long-term confidence - even in a changing market. 
            </p>
              <ul className="mt-5 space-y-2 text-white/90 text-xl">
                <li>• Data-led insights - not sales pitches
</li>
                <li>• Independent, expert advice from a £500m+ track record
</li>
                <li>• End-to-end guidance from strategy to acquisition</li>
              </ul>
            </div>

            {/* Right form card */}
            <div className="rounded-2xl bg-white/95 backdrop-blur border border-slate-200 p-5 sm:p-6 shadow-xl w-full max-w-md mx-auto lg:mx-0">
              <h2 className="text-xl font-semibold text-[#003366]">Sign up to the seminar</h2>
              <p className="mt-1 text-sm text-slate-600">Reserve your place - we’ll send joining details and a reminder.</p>

              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    required
                    className="h-11 rounded border px-3"
                    placeholder="Full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    autoComplete="name"
                    disabled={loading}
                  />
                  <input
                    required
                    type="email"
                    className="h-11 rounded border px-3"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    disabled={loading}
                  />
                </div>

                {/* Phone — exactly mirrors Landing/Enquire */}
<label className="block text-sm font-medium text-[#003366]">Phone</label>
<div className="mt-1 flex flex-col sm:flex-row gap-2 items-stretch">
  <select
    name="countryCode"
    required
    value={countryCode}
    onChange={(e) => {
      const cc = e.target.value;
      setCountryCode(cc);
      setPhoneLocal((prev) => scrubLocal(prev, cc));
    }}
    className="rounded border px-3 py-2 h-11 w-full sm:w-32 md:w-40 shrink-0"
    disabled={loading}
    autoComplete="tel-country-code"
  >
    {textOnlyOptions().map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>

  <input
    name="phoneLocal"
    type="tel"
    value={phoneLocal}
    onChange={(e) => setPhoneLocal(scrubLocal(e.target.value, countryCode))}
    onPaste={(e) => {
      const t = (e.clipboardData || window.clipboardData).getData("text");
      setPhoneLocal(scrubLocal(t, countryCode));
      e.preventDefault();
    }}
    className="rounded border px-3 py-2 h-11 w-full flex-1 min-w-0"
    placeholder="Phone"
    autoComplete="tel-national"
    inputMode="numeric"
    pattern="[0-9]*"
    required
    disabled={loading}
  />
</div>




                {/* EXACT SAME QUALIFIERS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select
                    value={readyToInvest}
                    onChange={(e) => setReadyToInvest(e.target.value)}
                    className="h-11 rounded border px-3"
                    required
                    disabled={loading}
                  >
                    <option value="">When are you planning to make your next investment?</option>
                    <option>Ready to invest now.</option>
                    <option>Within 3 months.</option>
                    <option>3-6 months.</option>
                    <option>Just exploring for now.</option>
                  </select>
                  <select
                    value={amountToInvest}
                    onChange={(e) => setAmountToInvest(e.target.value)}
                    className="h-11 rounded border px-3"
                    required
                    disabled={loading}
                  >
                    <option value="">What level of funds are you planning to allocate?</option>
                    <option>Under £100,000</option>
                    <option>£100,000 - £250,000</option>
                    <option>£250,000 - £500,000</option>
                    <option>£500,000 plus</option>
                    <option>Unknown</option>
                  </select>
                </div>
<div>
  <label className="block text-sm font-medium text-[#003366]">Message (optional)</label>
  <textarea
    name="message"
    value={message}
    onChange={(e) => setMessage(e.target.value)}
    rows={3}
    placeholder="Any specific questions or goals you'd like to discuss?"
    className="mt-1 w-full rounded border px-3 py-2 text-sm"
    disabled={loading}
  />
</div>

                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={loading} />
                  I agree to be contacted about this seminar and related services.
                </label>

                {err && <p className="text-sm text-red-600">{err}</p>}
                {success && (
                  <div className="p-3 rounded bg-green-50 border border-green-200 text-green-700">
                    <strong>Thanks — your spot is reserved.</strong>
                    <p className="text-sm">
                      We’ve emailed your confirmation and will share joining details closer to{" "}
                      {new Date(SEMINAR_DATE).toLocaleDateString()}.
                    </p>
                  </div>
                )}

                {/* Submit + gated Calendly — mirrors Landing */}
                <div className="flex gap-3">
                  <button type="submit" disabled={loading} className="h-11 px-6 rounded bg-[#003366] text-white">
                    {loading ? "Submitting…" : "Sign up"}
                  </button>

                 <button
  type="button"
  onClick={openCalendlyOrHint}
  className="inline-flex items-center px-4 rounded border border-[#003366] text-[#003366]"
  aria-label="Book a call on Calendly"
>
  Book a call
</button>

                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CLIENT TESTIMONIAL VIDEOS */}
<section className="max-w-6xl mx-auto px-6 pt-8 pb-4 sm:pt-10 sm:pb-6">
  <h2 className="text-2xl font-semibold text-[#003366]">Client stories</h2>
  <p className="mt-1 text-sm text-slate-600">Hear how investors work with Ethira.</p>

  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
    <video
      className="w-full rounded-xl border shadow-sm"
      controls
      playsInline
      preload="none"
      poster={poster1}
    >
      <source src={testimonial1_1080} type="video/mp4" />
      Your browser does not support the video tag.
    </video>

    <video
      className="w-full rounded-xl border shadow-sm"
      controls
      playsInline
      preload="none"
      poster={poster2}
    >
      <source src={testimonial2_1080} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  </div>
</section>


      {/* TRUSTPILOT */}
      <section className="max-w-6xl mx-auto px-6 pt-6 pb-8 sm:pt-8 sm:pb-10">
        <TrustpilotReviews />
      </section>

      {/* WHY WORK WITH US */}
      <section className="max-w-6xl mx-auto px-6 pt-4 pb-10">
        <h2 className="text-2xl font-semibold text-[#003366]">Why work with us</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">Independent</h3>
            <p className="mt-2 text-sm text-slate-700">We’re not tied to a single developer, so we recommend what suits your plan — not ours.</p>
          </div>
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">Bespoke guidance</h3>
            <p className="mt-2 text-sm text-slate-700">1:1 advice based on your goals, timeframe and risk appetite — backed by experience, not guesswork.</p>
          </div>
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">360° service</h3>
            <p className="mt-2 text-sm text-slate-700">Finance, legals, surveys, furnishing and lets — a joined-up journey from start to finish.</p>
          </div>
          <div className="rounded-xl border p-5">
            <h3 className="font-semibold text-[#003366]">Trusted by investors</h3>
            <p className="mt-2 text-sm text-slate-700">From first-time buyers to portfolio builders — long-term relationships and measurable outcomes.</p>
          </div>
        </div>
      </section>

      {/* FINAL CTA — ungated Calendly (no prefill), mirrors Landing footer */}
      <section className="bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-10 sm:py-12">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-[#003366]">Want to speak sooner?</h3>
                  <p className="text-sm text-slate-700 mt-1">Book a quick call with the team.</p>
                </div>
                <div className="flex w-full md:w-auto gap-3 md:justify-end">
                  
                    
                  <button
  type="button"
  onClick={openCalendlyOrHint}
  className="inline-flex items-center px-4 rounded border border-[#003366] text-[#003366]"
  aria-label="Book a call on Calendly"
>
  Book a call
</button>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="max-w-6xl mx-auto px-6 pb-10 text-xs text-slate-500">
        Capital at risk. Past performance is not a reliable indicator of future results. This page is for information
        only and does not constitute regulated financial advice.
      </section>
    </main>
  );
}
